// Extractant factors are served by compute, never hardcoded. Read from_m3 directly and never invert
// to_m3: the display lens converts stored Mehlich-3 into the requested extractant, and a guessed
// multiplier would disagree with the rest of the platform.

export interface FactorEntry {
  factor: number;
}
export interface ExtractantFactors {
  version: string;
  to_m3: Record<string, Record<string, FactorEntry>>;
  from_m3: Record<string, Record<string, FactorEntry>>;
  not_converted: string[];
  shared_cation_tokens?: string[];
}

/* * The analytes a lens can convert. Ca and Mg are their OWN analytes now, not aliases of K. */
export type FactorAnalyte = "p" | "k" | "ca" | "mg";

/*
 *
 * Our picker token -> compute's factor key.
 *
 * They key the cation factors on `nh4oac`; our picker says `ammonium_acetate_dtpa` (and `ammonium_acetate`
 * survives in older data). Their `shared_cation_tokens` says both mean the same conversion, which is why
 * this maps rather than matching on spelling.
 */
export function factorKeyFor(
  analyte: FactorAnalyte,
  token: string,
): string | null {
  if (token === "m3") return null; // identity by definition — no factor to look up
  if (analyte === "p")
    return token === "bray" || token === "olsen" ? token : null;
  return token === "ammonium_acetate" || token === "ammonium_acetate_dtpa"
    ? "nh4oac"
    : null;
}

/*
 *
 * FROM the stored M3 value TO the display extractant, for one analyte.
 *
 * `null` means "no conversion published for this pair" — the caller must then show the stored value
 * labelled as M3, never a guess. `1` is only ever returned for M3 itself, which is a true identity rather
 * than a missing factor; those look identical in the output and mean opposite things.
 */
export function displayFactor(
  factors: ExtractantFactors | null | undefined,
  analyte: FactorAnalyte,
  token: string,
): number | null {
  if (token === "m3") return 1;
  if (!factors) return null; // not fetched yet — we do not convert on a remembered number
  const key = factorKeyFor(analyte, token);
  if (!key) return null;
  const f = factors.from_m3?.[analyte]?.[key]?.factor;
  return typeof f === "number" && Number.isFinite(f) && f > 0 ? f : null;
}

/*
 *
 * TO Mehlich-3, from a value whose source extractant is KNOWN.
 *
 * The mirror of `displayFactor`, and the same rule applies: read `to_m3` directly rather than inverting
 * `from_m3`. This exists for SAMPLE values, which are stored in whatever extractant the lab used — unlike
 * modelled layers, which are already M3. Getting a sample onto M3 is what makes the depth chain (fitted on
 * M3) and the display lens applicable to it at all.
 *
 * `null` means "we cannot put this on a known basis" and the caller must NOT convert. That covers a missing
 * token and, deliberately, `bray_olsen` — which means *Bray or Olsen, undetermined*. Bray and Olsen carry
 * genuinely different factors, so treating that token as either is a guess that moves a real number.
 */
export function toM3Factor(
  factors: ExtractantFactors | null | undefined,
  analyte: FactorAnalyte,
  labToken: string | null | undefined,
): number | null {
  if (!labToken) return null;
  if (labToken === "m3") return 1; // identity by definition
  if (!factors) return null;
  const key = factorKeyFor(analyte, labToken); // returns null for `bray_olsen` — exactly the refusal we want
  if (!key) return null;
  const f = factors.to_m3?.[analyte]?.[key]?.factor;
  return typeof f === "number" && Number.isFinite(f) && f > 0 ? f : null;
}
